import DOMPurify from "dompurify";
import { Card, Modal } from "react-bootstrap";
import {
  ActionBadge,
  ActorInfo,
  CardBodyContent,
  CardDescriptionText,
  CardHeaderContent,
  CardHeaderLeft,
  CardHeaderRight,
  CardsContainer,
  CardTitleStyled,
  ChangeGrid,
  ChangeItem,
  ChangeLabelAfter,
  ChangeLabelBefore,
  ChangeSeparator,
  ChangesSection,
  ChangesTitle,
  ChangeValueAfter,
  ChangeValueBefore,
  ChangeValueContainer,
  FieldName,
  FieldNameHighlight,
  ObjectIdText,
  ResourceBadge,
  ResourceTypeInfo,
  StyledCard,
  StyledModalBody,
  TimestampInfo,
} from "./history-modal-styles";
import DescriptionIcon from "@material-ui/icons/Description";
import ArrowRightAlt from "@material-ui/icons/ArrowRightAlt";
import { ActionsType, FlowHistoryData } from "../../../../@types/flow-history";
import Person from "@material-ui/icons/Person";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { returnLabel } from ".";
import { renderValueText } from "../../../../utils/renderValueText";

type HistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  item: FlowHistoryData;
};

const HistoryModal = ({ isOpen, onClose, item }: HistoryModalProps) => {
  const flowHistory = item;

  const getActionColor = (action: ActionsType) => {
    switch (action) {
      case "criacao":
        return "success";
      case "atualizacao":
        return "warning";
      case "remocao":
        return "danger";
      case "desativacao":
        return "danger";
      default:
        return "secondary";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <DescriptionIcon className="h-5 w-5" />
          Histórico do fluxo
        </Modal.Title>
      </Modal.Header>
      <StyledModalBody>
        <CardsContainer>
          <StyledCard>
            <Card.Header>
              <CardHeaderContent>
                <CardHeaderLeft>
                  <div className="d-flex align-items-center gap-2">
                    <ActionBadge color={getActionColor(flowHistory?.action)}>
                      {returnLabel[flowHistory?.action] ?? flowHistory?.action}
                    </ActionBadge>
                    <ObjectIdText>ID: {flowHistory?.object_id}</ObjectIdText>
                  </div>
                  <CardTitleStyled>
                    {renderValueText(
                      flowHistory?.resource?.representation_name,
                      "Sem informação."
                    )}
                  </CardTitleStyled>
                  <CardDescriptionText>
                    {renderValueText(
                      flowHistory?.description,
                      "Sem informação."
                    )}
                  </CardDescriptionText>
                </CardHeaderLeft>
                <CardHeaderRight>
                  <ActorInfo>
                    <Person style={{ fontSize: 16 }} />
                    <span>
                      {flowHistory?.actor ? (
                        <>
                          {flowHistory?.actor?.first_name}{" "}
                          {flowHistory?.actor?.last_name}
                        </>
                      ) : (
                        "Sem informação."
                      )}
                    </span>
                  </ActorInfo>
                  <TimestampInfo>
                    <ScheduleIcon style={{ fontSize: 16 }} />
                    <span>
                      {renderValueText(
                        formatDate(flowHistory?.created_at),
                        "Sem informação."
                      )}
                    </span>
                  </TimestampInfo>
                </CardHeaderRight>
              </CardHeaderContent>
            </Card.Header>
            <CardBodyContent>
              <ResourceTypeInfo>
                <span>Tipo de Recurso:</span>
                <ResourceBadge>
                  {renderValueText(
                    flowHistory?.resource?.verbose_name,
                    "Sem informação."
                  )}
                </ResourceBadge>
              </ResourceTypeInfo>

              {Object?.keys(flowHistory.changes)?.length > 0 ? (
                <>
                  <hr />
                  <ChangesSection>
                    <ChangesTitle>Alterações Realizadas:</ChangesTitle>
                    {Object.entries(flowHistory.changes).map(
                      ([field, change]) => (
                        <ChangeItem key={field}>
                          <FieldName>
                            Campo:{" "}
                            <FieldNameHighlight>{field}</FieldNameHighlight>
                          </FieldName>
                          <ChangeGrid>
                            <ChangeValueContainer>
                              <ChangeLabelBefore>Antes:</ChangeLabelBefore>
                              <ChangeValueBefore
                                dangerouslySetInnerHTML={{
                                  __html:
                                    DOMPurify.sanitize(change.before) ||
                                    "(vazio)",
                                }}
                              />
                            </ChangeValueContainer>
                            <ChangeSeparator>
                              <ArrowRightAlt
                                style={{ color: "#6c757d", fontSize: 16 }}
                              />{" "}
                            </ChangeSeparator>
                            <ChangeValueContainer>
                              <ChangeLabelAfter>Depois:</ChangeLabelAfter>
                              <ChangeValueAfter
                                dangerouslySetInnerHTML={{
                                  __html:
                                    DOMPurify.sanitize(change.after) ||
                                    "(vazio)",
                                }}
                              />
                            </ChangeValueContainer>
                          </ChangeGrid>
                        </ChangeItem>
                      )
                    )}
                  </ChangesSection>
                </>
              ) : (
                <>
                  <hr />
                  <ChangesSection>
                    <ChangesTitle>Sem alterações realizadas</ChangesTitle>
                  </ChangesSection>
                </>
              )}
            </CardBodyContent>
          </StyledCard>
        </CardsContainer>
      </StyledModalBody>
    </Modal>
  );
};

export default HistoryModal;
